import Script from 'next/script';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { twMerge } from 'tailwind-merge';

import Spinner from 'shopper/components/Spinner';

import { getYTVideoId } from 'lib/utils';

import THIRD_PARTY from 'constants/thirdParty';

const YOUTUBE_URL = 'https://www.youtube.com';

const YOUTUBE_THUMBNAIL_URL = 'https://img.youtube.com';
const DEFAULT_IFRAME_TITLE = 'iniciar vídeo';
const DEFAULT_IFRAME_HEIGHT_IN_PX = 450;
const DEFAULT_IFRAME_WIDTH_IN_PX = '100%';

const getThumbnailImg = (videoId) =>
  `${YOUTUBE_THUMBNAIL_URL}/vi/${videoId}/0.jpg`;

const sanitizeUrl = (url) => {
  if (!url.startsWith('http')) {
    return `https://${url.replace(/^\/\//m, '')}`;
  }

  return url;
};

const getSize = ({ iframe, src }) => {
  if (src) {
    return {
      height: DEFAULT_IFRAME_HEIGHT_IN_PX,
      width: DEFAULT_IFRAME_WIDTH_IN_PX,
    };
  }

  const width =
    iframe.match(/(?:\/width=").+?(?=")/)[0] ?? DEFAULT_IFRAME_WIDTH_IN_PX;
  const height =
    iframe.match(/(?:\/height=").+?(?=")/)[0] ?? DEFAULT_IFRAME_HEIGHT_IN_PX;

  return { width: Number(width), height: Number(height) };
};

const createFacade = ({ iframe, src }) => {
  if (!iframe && !src) {
    return { thumbnailImageUrl: '', source: '', size: '' };
  }

  const source = iframe
    ? sanitizeUrl(iframe.match(/(?:\/src=").+?(?=")/)[0])
    : src;
  const videoId = getYTVideoId(source);
  const thumbnailImageUrl = getThumbnailImg(videoId);
  const size = getSize({ iframe, src });
  const srcWithParams = `${src}${THIRD_PARTY.YT_PARAMS}`;

  return { thumbnailImageUrl, source: srcWithParams, size };
};

const YoutubeFacade = ({
  className,
  full = false,
  height = null,
  iframe = null,
  src = null,
  title = null,
  width = null,
  ...rest
}) => {
  const [shouldRenderIframe, setShouldRenderIframe] = useState(false);
  const [isLoading, setLoading] = useState(false);

  const { source, size, thumbnailImageUrl } = createFacade({
    iframe,
    src,
  });

  const commonStyles = {
    height: height ?? size.height,
    width: width ?? size.width,
  };

  const onEmbedClick = () => {
    setShouldRenderIframe(true);
    setLoading(true);
  };

  const onAfterIframeLoad = () => setLoading(false);

  if (!source) {
    return null;
  }

  return (
    <>
      <Script id="youtube-preconnection" rel="preconnect" src={YOUTUBE_URL} />
      <div
        className={twMerge(
          'flex items-center justify-center',
          full && 'w-full',
          className
        )}
        style={{ ...commonStyles }}
        {...rest}
      >
        {isLoading && <Spinner className="absolute" />}
        {!shouldRenderIframe ? (
          <button
            aria-label="Iniciar vídeo"
            className="relative size-full before:block before:size-full before:bg-[image:var(--before-bg-image-url)] before:bg-cover before:bg-[center_center] before:opacity-90 before:blur-[3px] before:content-empty after:absolute after:left-[calc(50%_-_15px)] after:top-[calc(50%_-_30px)] after:z-10 after:size-0 after:rotate-90 after:border-[20px] after:border-b-[40px] after:border-t-0 after:border-solid after:border-transparent after:border-b-neutral-high-100 after:drop-shadow-[6px_8px_16px_rgba(0,0,0,0.3)] after:content-empty after:hover:opacity-80 after:hover:transition-all after:hover:duration-150 after:hover:ease-in"
            /**
             * This is better than using style prop for the dynamic `before:bg`
             * directly
             */
            style={{ '--before-bg-image-url': `url(${thumbnailImageUrl})` }}
            onClick={onEmbedClick}
          />
        ) : (
          <iframe
            allow={THIRD_PARTY.IFRAME_PERMISSIONS}
            className="border-none"
            src={source}
            title={title ?? DEFAULT_IFRAME_TITLE}
            allowFullScreen
            onLoad={onAfterIframeLoad}
            {...commonStyles}
          />
        )}
      </div>
    </>
  );
};

YoutubeFacade.propTypes = {
  className: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  full: PropTypes.bool,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  iframe: PropTypes.string,
  src: PropTypes.string,
  title: PropTypes.string,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default YoutubeFacade;
